// draftService.js - FIXED VERSION with request deduplication and retry logic
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

// Request deduplication cache to prevent multiple simultaneous requests
const requestCache = new Map();

// Helper function to create cache key
const createCacheKey = (url, method = 'GET') => `${method}:${url}`;

// Helper function to make deduplicated requests
const makeRequest = async (url, options = {}) => {
  const method = options.method || 'GET';
  const cacheKey = createCacheKey(url, method);
  
  // If request is already in flight, return the existing promise
  if (requestCache.has(cacheKey)) {
    console.log(`Reusing existing request for ${cacheKey}`);
    return requestCache.get(cacheKey);
  }
  
  // Create new request promise
  const requestPromise = fetch(url, options)
    .finally(() => {
      // Remove from cache when request completes (success or failure)
      requestCache.delete(cacheKey);
    });
  
  // Cache the promise
  requestCache.set(cacheKey, requestPromise);
  
  return requestPromise;
};

// Helper function for retry logic
const retryRequest = async (requestFn, maxRetries = 3) => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      const response = await requestFn();
      
      // Check for rate limiting
      if (response.status === 429 && attempt < maxRetries) {
        const delay = 1000 * Math.pow(2, attempt); // Exponential backoff
        console.warn(`Rate limited, retrying in ${delay}ms... (attempt ${attempt + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      return response;
      
    } catch (error) {
      // Retry on network errors
      if (attempt < maxRetries && 
          (error.name === 'NetworkError' || 
           error.message.includes('Connection') ||
           error.message.includes('fetch'))) {
        const delay = 1000 * Math.pow(2, attempt);
        console.warn(`Network error, retrying in ${delay}ms... (attempt ${attempt + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      throw error;
    }
  }
};

// Save or update draft
export const saveDraft = async (draftData, getAccessToken) => {
  try {
    const accessToken = await getAccessToken();
    const authHeader = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    
    const url = `${API_BASE_URL}/saveDraft`;
    const response = await makeRequest(url, {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(draftData)
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || `HTTP ${response.status}: Failed to save draft`);
    }

    return result;
  } catch (error) {
    console.error('Error saving draft:', error);
    throw new Error(error instanceof Error ? error.message : 'Failed to save draft');
  }
};

// Load specific draft - FIXED with deduplication and retry logic
export const loadDraft = async (draftId, getAccessToken) => {
  try {
    console.log(`Loading draft ${draftId}`);
    
    const accessToken = await getAccessToken();
    const authHeader = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    
    const url = `${API_BASE_URL}/loadDraft/${draftId}`;
    
    // Use retry logic for loading drafts
    const response = await retryRequest(async () => {
      return makeRequest(url, {
        method: 'GET',
        headers: {
          'Authorization': authHeader,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Draft not found or access denied');
      }
      if (response.status === 401) {
        throw new Error('Unauthorized access');
      }
      
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to load draft'}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || result.error || 'Failed to load draft');
    }
    
    console.log(`Draft ${draftId} loaded successfully`);
    return result.data; // Return just the data part
    
  } catch (error) {
    console.error(`Error loading draft ${draftId}:`, error);
    throw error;
  }
};

// List all user drafts
export const listDrafts = async (getAccessToken) => {
  try {
    const accessToken = await getAccessToken();
    const authHeader = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    
    const url = `${API_BASE_URL}/listDrafts`;
    const response = await makeRequest(url, {
      method: 'GET',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.message || `HTTP ${response.status}: Failed to list drafts`);
    }

    return result;
  } catch (error) {
    console.error('Error listing drafts:', error);
    throw error;
  }
};

// Delete draft
export const deleteDraft = async (draftId, getAccessToken) => {
  try {
    const accessToken = await getAccessToken();
    const authHeader = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    
    const url = `${API_BASE_URL}/delete_drafts/${draftId}`;
    const response = await makeRequest(url, {
      method: 'DELETE',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Draft not found or already deleted');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to delete draft'}`);
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error deleting draft:', error);
    throw error;
  }
};

// Submit draft as gate pass request
export const submitDraft = async (draftId, getAccessToken) => {
  try {
    const accessToken = await getAccessToken();
    const authHeader = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    
    const url = `${API_BASE_URL}/submitDraft/${draftId}`;
    const response = await makeRequest(url, {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Draft not found or access denied');
      }
      if (response.status === 400) {
        const result = await response.json();
        throw new Error(result.error || 'Draft is incomplete and cannot be submitted');
      }
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to submit draft'}`);
    }

    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || result.error || 'Failed to submit draft');
    }

    return result;
  } catch (error) {
    console.error('Error submitting draft:', error);
    throw error;
  }
};

// Validation helpers for frontend (unchanged)
export const validateDraftCompletion = (formData, selectedGates = [], selectedPersonnel = []) => {
  const completion = {
    basicInfo: !!(formData.name && formData.email && formData.companyName && formData.idDetails && formData.regionId),
    requestDetails: !!(formData.consignmentDetails && formData.requestType && formData.passType),
    approvers: !!(formData.approverEmail && formData.approverUserId),
    gates: selectedGates.length > 0,
    personnel: selectedPersonnel.length > 0,
    validDateRange: !formData.startDate || !formData.endDate || (new Date(formData.endDate) >= new Date(formData.startDate))
  };

  const requiredSections = ['basicInfo', 'requestDetails', 'approvers', 'gates', 'personnel', 'validDateRange'];
  const completedSections = requiredSections.filter(section => completion[section]);
  const completionPercentage = Math.round((completedSections.length / requiredSections.length) * 100);
  const isSubmittable = requiredSections.every(section => completion[section]);

  return {
    completion,
    completionPercentage,
    isSubmittable,
    completedSections: completedSections.length,
    totalSections: requiredSections.length
  };
};

// Generate suggested draft names (unchanged)
export const generateDraftName = (formData) => {
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });

  if (formData.companyName) {
    return `${formData.companyName} - ${dateStr}`;
  } else if (formData.contractNumber) {
    return `Contract ${formData.contractNumber} - ${dateStr}`;
  } else {
    return `Gate Pass Draft - ${dateStr}`;
  }
};

// Utility function to clear request cache (useful for debugging)
export const clearRequestCache = () => {
  requestCache.clear();
  console.log('Request cache cleared');
};